import React, { useEffect, useState, useRef } from 'react';
import { VStack, Button, Text, Box, InputGroup, Input, HStack, IconButton, Avatar, Switch } from '@chakra-ui/react';
import { Room, User } from '../types';
import { FaUsers, FaPlus, FaPlusCircle, FaBaseballBall } from 'react-icons/fa';
import UserAvatar from '../../../components/UserAvatar';

interface UsersTabProps {
  markedUsers: User[];
  users: User[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onUserSelect: (user_id: string) => void;
  hoverBg: string;
  textColor: string;
  secondaryText: string;
  inputBg: string;
  pickPalette: (name: string) => string;
  onGroupChatOpen: () => void;
  groupChats: Room[];
  joinExistingGroupChat: (chatRoomId: string, members: string[], chatName: string, users: User[]) => void;
  rooms: Room[];
  onJoinDirectChat: (roomId: string) => void;
  selectedRoom?: string | null;
  hideCreateButton?: boolean;
}

export const UsersTab: React.FC<UsersTabProps> = ({
  markedUsers,
  searchQuery,
  onSearchChange,
  hoverBg,
  textColor,
  secondaryText,
  inputBg,
  onGroupChatOpen,
  groupChats,
  joinExistingGroupChat,
  rooms,
  onJoinDirectChat,
  selectedRoom,
  hideCreateButton = false
}) => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Merge direct rooms and group rooms into one array
    const directRooms = rooms.filter(room => room.type === 'direct');
    const mergedRooms = [...directRooms, ...groupChats];
    const supportRooms = rooms.filter(room => room.type === 'support');
    const pinnedRooms = rooms.filter(room => room.type === 'league');

    // Filter and sort other rooms by last message time (most recent first)
    const roomsWithMessages = mergedRooms.filter(room => room.last_message_at);

    const sortedOtherRooms = roomsWithMessages.sort((a, b) => {
      return b?.last_message_at?.localeCompare(a?.last_message_at) || 0;
    });

    // Combine: support rooms first, then pinned league rooms, then sorted other rooms
    const sortedRooms = [...supportRooms, ...pinnedRooms, ...sortedOtherRooms];

    setAllRooms(sortedRooms);
  }, [rooms, groupChats]);

  useEffect(() => {
    const searchLower = searchQuery.toLowerCase();
    const filteredRooms = allRooms.filter(room => {
      return room.name?.toLowerCase().includes(searchLower) || room.other_user?.username?.toLowerCase().includes(searchLower) || room.other_user?.email?.toLowerCase().includes(searchLower);
    });
    setFilteredRooms(filteredRooms);
  }, [searchQuery])

  useEffect(() => {
    // Filter all rooms (both direct and group) based on unread status
    setFilteredRooms(unreadOnly ? allRooms.filter(room => room.unread) : allRooms);
  }, [unreadOnly, allRooms]);

  // Scroll to bottom only on initial mount
  useEffect(() => {
    if (scrollContainerRef.current && filteredRooms.length > 0) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount
  return (
    <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
      <HStack pr={4} pl={4} pt={4} justifyContent="space-between" alignItems="center" flexShrink={0}>
        <InputGroup>
          <Input
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            bg={inputBg}
            color={textColor}
            borderRadius="lg"
            _placeholder={{ color: secondaryText }}
          />
        </InputGroup>
        <HStack>
          <Text fontSize="xs" color={secondaryText}>
            Unread Only
          </Text>
          <Switch
            pl={2}
            checked={unreadOnly}
            colorScheme="green"
            onChange={() => setUnreadOnly(!unreadOnly)}
          />
        </HStack>
      </HStack>
      <VStack
        ref={scrollContainerRef}
        flex="1"
        overflowY="auto"
        p={4}
        gap={2}
        align="stretch"
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '24px',
          },
        }}
      >
        {!hideCreateButton && (
          <Button
            onClick={() => onGroupChatOpen()}
            variant="ghost"
            justifyContent="flex-start"
            _hover={{ bg: hoverBg }}
            h="auto"
            p={3}
            borderRadius="lg"
            bg="whiteAlpha.100"
            color={textColor}
          >
            <HStack gap={3}>
              <Avatar icon={<FaPlusCircle size={28} color="white" />} bg="#002e0b" />
              <VStack align="start" gap={0}>
                <Text fontWeight="medium">
                  Create A New Chat
                </Text>
                <Text fontSize="xs" color={secondaryText}>
                  Start a new conversation
                </Text>
              </VStack>
            </HStack>
          </Button>
        )}
        {filteredRooms.map((room) => {
          const isSelected = selectedRoom === room.id;
          return (
            <Box
              key={room.id}
              onClick={() => {
                if (room.type === 'direct') {
                  onJoinDirectChat(room.id);
                } else {
                  joinExistingGroupChat(room.id, room.members, room.name, room.users);
                }
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              cursor="pointer"
              _hover={{ bg: hoverBg }}
              h="auto"
              p={3}
              borderRadius="lg"
              bg={isSelected ? 'whiteAlpha.500' : (markedUsers.some(markedUser => markedUser.id === room.id) ? 'whiteAlpha.200' : 'whiteAlpha.100')}
              border={isSelected ? '2px solid' : 'none'}
              borderColor={isSelected ? '#002e0b' : 'transparent'}
              color={textColor}
              position="relative"
            >
              {(markedUsers.some(markedUser => markedUser.id === room.id) || room.unread) && (
                <Box
                  position="absolute"
                  top="50%"
                  right={3}
                  transform="translateY(-50%)"
                  w="2"
                  h="2"
                  borderRadius="full"
                  bg="red.500"
                />
              )}
              <HStack gap={3}>
                {room.type === 'direct' ? (
                  <UserAvatar user={room.other_user} size="md" canEdit={false} />
                ) : room.type === 'support' ? (
                  <Avatar icon={<FaBaseballBall size={28} color="white" />} bg="#002e0b" />
                ) : (
                  <Avatar icon={<FaUsers size={28} color="white" />} bg="#002e0b" />
                )}
                <VStack align="start" gap={0}>
                  <Text fontWeight="medium" align="left">
                    {room.type === 'direct' ? room.other_user?.username : room.name}
                  </Text>
                  {room.type === 'group' && (
                    <Text fontSize="xs" color={secondaryText}>
                      Group chat
                    </Text>
                  )}
                  {room.type === 'support' && (
                    <Text fontSize="xs" color={secondaryText}>
                      Support
                    </Text>
                  )}
                </VStack>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}; 